import React from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COMPONENTS
import ActionFloorButton from "./ActionFloorButton";
import UnnamedFloorWarning from "./UnnamedFloorWarning";
// INTERFACES
import { BankFloorInterface } from "../../../../interfaces/Bank.interface";

/**
 * The floor selector on a row whose command takes a position.
 *
 * This is a SELECTOR, not a dispatcher — that is the whole difference from the
 * `ActionFloorCard` it grew out of. That card was a second, separate row emitted
 * below the list: picking a floor on it sent the command immediately, and the
 * configured row it stood in for was removed from the list entirely, taking the
 * operator's label, button type, valid-state gating and colours with it. Two
 * rows bound to the same Control collapsed into one, and the Actions list
 * stopped matching the setup wizard's Actions table row for row.
 *
 * Here the row keeps its own card and its own buttons. The strip only answers
 * "which floor", and the row's configured commands dispatch there — so a
 * positional command finally goes to a position the operator chose, instead of
 * the hardcoded 0 it used to carry.
 *
 * Eligibility (door side, top/bottom exclusion, per-lift service, RBAC floor
 * scope) is entirely the caller's business; this component draws the floors it
 * is handed and reports which one was picked.
 */
export default function ActionFloorStrip({
  floors,
  selectedFloorIndex,
  onSelectFloor,
  disabled = false,
}: {
  /** Eligible floors, already filtered and ordered by the caller. */
  floors: BankFloorInterface[];
  /** The picked floor's `floorIndex`, or null while nothing is picked. */
  selectedFloorIndex: number | null;
  onSelectFloor: (floorIndex: number) => void;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");

  return (
    <div className="actionCard__floorSelect">
      <span className="actionCard__floorSelectLabel">
        {t("monitoringActions.selectFloor", "Select a floor to send the command")}
        {/* Sits with the label rather than on the buttons: the caveat is about
            the whole strip, and repeating it twelve times would bury it. */}
        <UnnamedFloorWarning floors={floors} />
      </span>
      {/* A radiogroup, not a row of independent buttons: exactly one floor is
          the command's target at a time, and a screen reader should say so
          rather than announcing N unrelated toggles. */}
      <div
        className="actionCard__floorStrip"
        role="radiogroup"
        aria-label={t("monitoringActions.selectFloor", "Select a floor to send the command")}
      >
        {floors.map((floor: BankFloorInterface) => (
          <ActionFloorButton
            key={floor.id}
            floor={floor}
            selected={selectedFloorIndex === floor.floorIndex}
            disabled={disabled}
            onClick={onSelectFloor}
          />
        ))}
      </div>
    </div>
  );
}
